import React from "react";
import {
  makeStyles,
  Button,
  Field,
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionPanel,
} from "@fluentui/react-components";
import DropdownField from "../../common/DropdownField/DropdownField";
import Modal from "../../common/Modal/Modal";
import MapboxMap from "../../common/MapboxMap/MapboxMap";
import MapboxSearchField from "../../common/MapboxSearchField/MapboxSearchField";
import ToggleField from "../../common/ToggleField/ToggleField";
import CustomerForm from "../../forms/CustomerForm/CustomerForm";
import RouteCard from "./RouteCard/RouteCard";
import RouteDetailsCard from "./RouteDetailsCard/RouteDetailsCard";
import useSearch from "./useSearch";

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    padding: "12px",
    width: "100%",
    height: "100%",
    overflowX: "hidden",
    overflowY: "auto",
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    width: "100%",
  },
  actionButton: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
  },
  inlineField: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: "8px",
    width: "100%",
  },
  controls: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
    padding: "8px 0",
  },
  resultContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    width: "100%",
  },
  fullWidth: {
    width: "100%",
  },
});

const DrayageSearch = () => {
  const styles = useStyles();
  const {
    isTerminalPickup,
    loading,
    newCustomerProfileLoading,
    tollsLoading,
    accordionOpenItems,
    isAddCustomerProfileFormOpen,
    isRouteDetailsCardOpen,
    // isPrintModalOpen,
    companyProfileOptions,
    pricingTierOptions,
    fscOptions,
    oeOptions,
    terminalPickupLocationValue,
    shippingLocationsOptions,
    initialCalculationData,
    calculationData,
    quotes,
    selectedQuote,
    // companyDetails,
    // tollBarriersData,
    // routeOrgSearchFieldRef,
    // routeDestSearchFieldRef,
    // leftContentRef,
    // routeCardsListRef,
    onToggleDeliveryType,
    onTerminalLocationChange,
    onLocationChange,
    onGetRouteEstimates,
    // onFetchCustomerProfiles,
    // onSearchShippingLocations,
    // onQuoteSelect,
    onOpenRouteDetailsCard,
    onCustomerProfileFormSubmit,
    setCalculationData,
    setUiState,
    isSearchEnabled,
    getFscFromPricingTier,
    getOeFromPricingTier,
    getQuotesTolls,
    setOfferPrice,
  } = useSearch();

  return (
    <div className={styles.container}>
      <div className={styles.field}>
        <Field
          className={styles.fullWidth}
          label={
            <div className={styles.actionButton}>
              Company Profile{" "}
              <Button
                appearance="transparent"
                size="small"
                onClick={() =>
                  setUiState({ isAddCustomerProfileFormOpen: true })
                }
              >
                + Add New
              </Button>
            </div>
          }
        >
          <DropdownField
            options={companyProfileOptions}
            selectedValue={calculationData.companyId}
            onSelect={(value) =>
              setCalculationData((prev) => ({
                ...prev,
                companyId: parseInt(value),
              }))
            }
            placeholder="Select company profile"
          />
        </Field>
      </div>

      <div className={styles.field}>
        <Field className={styles.fullWidth} label="Pricing Profile">
          <DropdownField
            options={pricingTierOptions}
            selectedValue={calculationData.tariffPricingTierId}
            onSelect={(value) => {
              const numValue = parseInt(value);
              setCalculationData((prev) => ({
                ...prev,
                tariffPricingTierId: numValue,
                fuelSurchargeProfileId: getFscFromPricingTier(numValue),
                operationalExpensesProfileId: getOeFromPricingTier(numValue),
              }));
            }}
            placeholder="Select pricing profile"
          />
        </Field>
      </div>

      <div className={styles.field}>
        <Field className={styles.fullWidth} label="Fuel Surcharge Profile">
          <DropdownField
            options={fscOptions}
            selectedValue={calculationData.fuelSurchargeProfileId}
            onSelect={(value) => {
              setCalculationData((prev) => ({
                ...prev,
                fuelSurchargeProfileId: parseInt(value),
              }));
            }}
            placeholder="Select fuel surcharge profile"
          />
        </Field>
      </div>

      <div className={styles.field}>
        <Field className={styles.fullWidth} label="Operation Expenses Profile">
          <DropdownField
            options={oeOptions}
            selectedValue={calculationData.operationalExpensesProfileId}
            onSelect={(value) => {
              setCalculationData((prev) => ({
                ...prev,
                operationalExpensesProfileId: parseInt(value),
              }));
            }}
            placeholder="Select operation expenses profile"
          />
        </Field>
      </div>

      <div className={`${styles.inlineField} ${styles.fullWidth}`}>
        <ToggleField
          checked={isTerminalPickup}
          onChange={onToggleDeliveryType}
          label="Terminal Pickup"
        />
      </div>

      <div className={styles.field}>
        <Field className={styles.fullWidth} label="Pickup Location">
          {isTerminalPickup ? (
            <DropdownField
              options={shippingLocationsOptions}
              selectedValue={terminalPickupLocationValue}
              onSelect={(value) => {
                const selectedOption = shippingLocationsOptions.find(
                  (opt) => opt.value === value,
                );
                if (selectedOption) {
                  onTerminalLocationChange(selectedOption, true);
                }
              }}
              placeholder="Select pickup location"
            />
          ) : (
            <MapboxSearchField
              onSelected={(location) => onLocationChange(location, true)}
            />
          )}
        </Field>
      </div>

      <div className={styles.field}>
        <Field className={styles.fullWidth} label="Delivery Location">
          <MapboxSearchField
            onSelected={(location) => onLocationChange(location, false)}
          />
        </Field>
      </div>

      <div className={styles.controls}>
        <Button
          appearance="secondary"
          disabled={loading === "pending"}
          onClick={() => setCalculationData(initialCalculationData)}
        >
          Clear
        </Button>
        <Button
          appearance="primary"
          disabled={!isSearchEnabled}
          onClick={onGetRouteEstimates}
        >
          Search
        </Button>
      </div>

      <Accordion
        collapsible
        openItems={accordionOpenItems}
        onToggle={(_e, data) => {
          setUiState({ accordionOpenItems: data.openItems as string[] });
        }}
      >
        <AccordionItem value="map">
          <AccordionHeader>Route Map</AccordionHeader>
          <AccordionPanel>
            <MapboxMap directionCoordinates={selectedQuote?.routeCoordinates} />
          </AccordionPanel>
        </AccordionItem>
      </Accordion>

      <div className={styles.resultContainer}>
        {quotes?.map((quote, index) => (
          <RouteCard
            isActive={selectedQuote?.quoteResultId === quote.quoteResultId}
            key={`route-card-${quote.quoteResultId}-${index}`}
            quote={quote}
            onQuoteSelect={() => onOpenRouteDetailsCard(quote)}
          />
        ))}
      </div>

      <Modal open={isRouteDetailsCardOpen} modalType="modal">
        <RouteDetailsCard
          quote={selectedQuote}
          tollsData={getQuotesTolls()}
          tollsLoading={tollsLoading}
          onHandleOfferPrice={setOfferPrice}
          onClose={() => setUiState({ isRouteDetailsCardOpen: false })}
        />
      </Modal>

      <Modal open={isAddCustomerProfileFormOpen} modalType="modal">
        <CustomerForm
          onSubmit={(data) => {
            onCustomerProfileFormSubmit(data);
            setUiState({ isAddCustomerProfileFormOpen: false });
          }}
          onCancel={() => {
            setUiState({ isAddCustomerProfileFormOpen: false });
          }}
          saving={newCustomerProfileLoading}
        />
      </Modal>
    </div>
  );
};

export default DrayageSearch;
